import React, {useRef, useCallback} from 'react';
import * as css from './InlineParamInput.css';
import {useParagraphEditor} from '../../hooks/useParagraphEditor';
import {isContentEmpty, focusNextEditableElement} from '../../utils/dom';
import {Helper} from '@tickchak/react-share/app';
import {useTranslation} from 'react-i18next';
import {getToken} from '@tickchak/react-share/app';

interface InlineParamInputProps {
    index: number;
    structureIndex: number;
    content: string;
    onChange: (index: number, htmlContent: string) => void;
    onFocus?: (index: number) => void;
    onEditableFocus?: (element: HTMLElement | null) => void;
    onEditableCursorMove?: () => void;
}

const InlineParamInput = React.memo(function InlineParamInput({index, structureIndex, content, onChange, onFocus, onEditableFocus, onEditableCursorMove}: InlineParamInputProps) {
    const {t} = useTranslation('whatsapp');
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const isEmpty = isContentEmpty(content);

    const handleContentChange = useCallback((htmlContent: string) => {
        onChange(index, htmlContent);
    }, [index, onChange]);

    const handleEnterKey = () => {
        if(contentEditableRef.current) {
            focusNextEditableElement(contentEditableRef.current);
        }
    };

    const {handleInput, handleKeyDown, handlePaste, handleBlur, handleFocus, handleCursorMove} = useParagraphEditor({
        content,
        contentEditableRef,
        onChange: handleContentChange,
        onFocus: () => onFocus?.(index),
        onEditableFocus,
        onEditableCursorMove,
        onEnterKey: handleEnterKey,
        enableMultiLinePaste: true
    });

    return (
        <>
            <css.inlineContentEditable
                ref={contentEditableRef}
                contentEditable
                data-structure-index={structureIndex}
                data-param-index={index}
                data-is-empty={String(isEmpty)}
                $isEmpty={isEmpty}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyUp={handleCursorMove}
                onMouseUp={handleCursorMove}
                onClick={handleCursorMove}
                suppressContentEditableWarning
            />
            {isEmpty && (
                <Helper
                    content={t('emptyParagraphWarning')}
                    size='small'
                    maxWidth={240}
                    icon={'!'}
                    bgColor={getToken('color.state.warning.main')}
                />
            )}
        </>
    );
});

export default InlineParamInput;
